import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { compact } from 'lodash-es';
import { TableList, Typography, TableListActions, DropdownMenuItem, useDevice, Column } from 'seagull';
import { AnchorGrayscale, Archive, Edit, Trash, Unarchive } from 'seagull/icons';
import ClientViewMenuItem from '~/components/ClientViewMenuItem/ClientViewMenuItem';
import EnterAccountantAccessButton from '~/components/EnterAccountantAccessButton/EnterAccountantAccessButton';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { AccountingMappingIndication } from '~/integrations/Accounting/AccountingMappingIndication/AccountingMappingIndication';
import { ContactAccountantAccess } from '~/models/ContactModel';
import { Accounting } from '~/types/api.model';
import { eligibilityTooltipProps, eligibilityTooltipsTextsPath, getAccountantAccessItem } from './ContactsDashboard.utils';
import styles from './ContactsDashboardRow.module.scss';
import { hasPortalAccess, portalAccessDisabledTooltip } from './ContactsDashboardRow.utils';
export default function ContactsDashboardRow(props) {
    var _contact_customerId, _contact_userId;
    const { contact, isQboIntegrated, isXeroIntegrated, qboCustomerIdToDisplay, xeroCustomerIdToDisplay, openContactModal, onRequestAccountantAccess, onCancelAccountantAccessRequest, onRemoveAccountantAccess, onGotoClientPortal, onRestoreContact, onArchiveContact, onDeleteContact, isUpdateInProgress = false } = props;
    const { ENABLE_KILL_ACCOUNTANT_ACCESS_FE, CRM_DRAFT_CONTACTS, FE_CRM } = useFeatureFlags();
    const { isMobileDevice } = useDevice();
    const accountantAccessItem = getAccountantAccessItem(contact.accountantAccess());
    const { t: clientViewTexts } = useLocaleKeys('components.clientView');
    const qboCustomerDisplay = contact.integratedId({
        accounting: Accounting.QBO
    }) ? qboCustomerIdToDisplay[contact.integratedId({
        accounting: Accounting.QBO
    })] : undefined;
    const xeroCustomerDisplay = contact.integratedId({
        accounting: Accounting.Xero
    }) ? xeroCustomerIdToDisplay[contact.integratedId({
        accounting: Accounting.Xero
    })] : undefined;
    const { t: eligibilityTooltipsTexts } = useLocaleKeys(eligibilityTooltipsTextsPath);
    const { t: rowTexts } = useLocaleKeys('pages.contactsDashboard.row');
    const { t: statusTexts } = useLocaleKeys('pages.contactsDashboard.contactStatusFilter');
    const showActionsMenu = !(FE_CRM && contact.isSample());
    const isEditDisabledWithoutClient = FE_CRM && contact.customerId() == null;
    const getEditButtonTooltip = ()=>{
        if (isUpdateInProgress) {
            return {
                title: rowTexts.updateInProgress()
            };
        }
        if (isEditDisabledWithoutClient) {
            return {
                title: rowTexts.editDisabledNoClient()
            };
        }
        return undefined;
    };
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: contact.id(),
        testId: contact.isSample() ? 'sample-contact' : "contact-row",
        "data-heapid": contact.isSample() ? 'sample-contact' : 'contact',
        items: compact([
            {
                value: /*#__PURE__*/ _jsxs("div", {
                    className: styles.companyName,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            weight: "normal",
                            testId: "contact-company-name",
                            children: contact.companyName()
                        }),
                        !ENABLE_KILL_ACCOUNTANT_ACCESS_FE && contact.accountantAccess() === ContactAccountantAccess.Invited && /*#__PURE__*/ _jsx(EnterAccountantAccessButton, {
                            variant: "pending",
                            testId: "acc-access-invited-indicator"
                        }),
                        !ENABLE_KILL_ACCOUNTANT_ACCESS_FE && contact.accountantAccess() === ContactAccountantAccess.Active && /*#__PURE__*/ _jsx(EnterAccountantAccessButton, {
                            testId: "acc-access-active-indicator",
                            onClick: ()=>onGotoClientPortal(contact)
                        }),
                        isQboIntegrated && /*#__PURE__*/ _jsx(AccountingMappingIndication, {
                            display: qboCustomerDisplay,
                            purpose: "customer",
                            accounting: Accounting.QBO
                        }),
                        isXeroIntegrated && /*#__PURE__*/ _jsx(AccountingMappingIndication, {
                            display: xeroCustomerDisplay,
                            purpose: "customer",
                            accounting: Accounting.Xero
                        })
                    ]
                })
            },
            {
                skin: 'standard',
                weight: 'normal',
                testId: 'contact-status',
                value: statusTexts[contact.status()]()
            },
            {
                ellipsis: true,
                skin: 'standard',
                testId: 'contact-contact-name',
                value: contact.fullName()
            },
            {
                value: /*#__PURE__*/ _jsxs(Column, {
                    gap: isMobileDevice ? 8 : 0,
                    testId: "contact-contact-details",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            skin: isMobileDevice ? 'secondary' : undefined,
                            variant: isMobileDevice ? undefined : 'paragraph2',
                            ellipsis: true,
                            children: contact.email()
                        }),
                        contact.phone() && /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            variant: isMobileDevice ? undefined : 'paragraph2',
                            children: contact.phone()
                        })
                    ]
                })
            }
        ]),
        actionColumn: showActionsMenu ? /*#__PURE__*/ _jsx(TableListActions, {
            testId: contact.isSample() ? 'sample-contact-actions' : 'table-list-actions',
            btnHeapId: contact.isSample() ? 'sample-contact-view-btn' : 'contact-view-btn',
            onClickButton: ()=>openContactModal(contact),
            mobile: {
                title: contact.companyName()
            },
            children: compact([
                !contact.isSample() && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'contact_edit'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                    onClick: ()=>openContactModal(contact),
                    testId: "table-list-actions-menu-item",
                    disabled: isUpdateInProgress || isEditDisabledWithoutClient,
                    tooltipProps: getEditButtonTooltip(),
                    children: "Edit"
                }, "view"),
                contact.isActive() ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'actions_archive'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Archive, {}),
                    onClick: ()=>onArchiveContact(contact),
                    testId: "actions-archive",
                    disabled: !contact.canArchive() || contact.isSample(),
                    tooltipProps: eligibilityTooltipProps(contact.archiveEligibility(), 'archive', eligibilityTooltipsTexts),
                    children: "Archive"
                }, "archive") : /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'actions_restore'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Unarchive, {}),
                    onClick: ()=>onRestoreContact(contact),
                    testId: "actions-restore",
                    children: "Unarchive"
                }, "restore"),
                !ENABLE_KILL_ACCOUNTANT_ACCESS_FE && accountantAccessItem && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'contact_accountant_access'
                    },
                    skin: accountantAccessItem.skin,
                    testId: "acc-access-".concat(accountantAccessItem.id),
                    iconStart: /*#__PURE__*/ _jsx(AnchorGrayscale, {}),
                    onClick: ()=>{
                        if (accountantAccessItem.id === 'request') {
                            contact.customerId() && onRequestAccountantAccess(contact);
                        } else if (accountantAccessItem.id === 'cancel') {
                            onCancelAccountantAccessRequest(contact);
                        } else {
                            onRemoveAccountantAccess(contact);
                        }
                    },
                    children: accountantAccessItem.text
                }, "accountant"),
                ENABLE_KILL_ACCOUNTANT_ACCESS_FE && !contact.isSample() && /*#__PURE__*/ _jsx(ClientViewMenuItem, {
                    clientBusinessId: (_contact_customerId = contact.customerId()) !== null && _contact_customerId !== void 0 ? _contact_customerId : null,
                    viewUserId: (_contact_userId = contact.userId()) !== null && _contact_userId !== void 0 ? _contact_userId : undefined,
                    disabled: !hasPortalAccess(contact, CRM_DRAFT_CONTACTS === true),
                    disabledTooltip: portalAccessDisabledTooltip({
                        contact,
                        clientViewTexts,
                        reviewerOnlyGate: CRM_DRAFT_CONTACTS === true
                    }),
                    testId: "contact-view-client-portal"
                }, "view-client-portal"),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    bi: {
                        name: 'actions_delete'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    onClick: ()=>onDeleteContact(contact),
                    testId: "actions-delete",
                    disabled: !contact.canDelete() || contact.isSample(),
                    skin: "error",
                    tooltipProps: eligibilityTooltipProps(contact.deleteEligibility(), 'delete', eligibilityTooltipsTexts),
                    children: "Delete"
                }, "delete")
            ])
        }) : undefined
    }, contact.id());
}
